<template>
  <!-- `attach="#app"`: o Tailwind sai com `important: '#app'` (padrão do PlaceholderDoctorBlockedModal). -->
  <v-dialog
    :model-value="modelValue"
    max-width="640"
    scrollable
    attach="#app"
    scrim="rgba(22, 22, 29, 0.55)"
    aria-labelledby="pre-allocation-confirm-title"
    data-testid="pre-allocation-confirm-dialog"
    @update:model-value="onDialogUpdate"
  >
    <div class="qualification-dialog">
      <header class="qualification-dialog__header">
        <span
          class="qualification-dialog__icon qualification-dialog__icon--alert"
          aria-hidden="true"
        >
          <svg viewBox="0 0 24 24" width="24" height="24">
            <path :d="mdiClockOutline" fill="currentColor" />
          </svg>
        </span>
        <div class="qualification-dialog__heading">
          <h2 id="pre-allocation-confirm-title" class="qualification-dialog__title">
            Pré-alocar {{ doctorName ?? 'este médico' }}?
          </h2>
          <span class="qualification-dialog__chip">
            <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
              <path :d="mdiAccountOutline" fill="currentColor" />
            </svg>
            {{ stationCount }} {{ isSingle ? 'plantão' : 'plantões' }}
          </span>
        </div>
      </header>

      <div class="qualification-dialog__scroll">
        <p class="qualification-dialog__intro">
          {{ doctorName ?? 'O médico' }} ainda não atende à Qualificação exigida desta escala, mas a
          escala permite pré-alocar enquanto a documentação é resolvida.
        </p>

        <div class="qualification-dialog__body">
          <p class="qualification-dialog__section">O que falta</p>
          <QualificationMissingList :missing="missing" />

          <p class="qualification-dialog__section">O que acontece ao confirmar</p>
          <!-- D68: sem aviso ativo ao gestor — só o médico é avisado. -->
          <ol class="qualification-dialog__steps">
            <li class="qualification-dialog__step">
              <span>
                {{ isSingle ? 'O plantão fica' : 'Os plantões ficam' }}
                <strong>“Pré-alocado · aguardando documentação”</strong> na grade.
              </span>
            </li>
            <li class="qualification-dialog__step">
              <span>
                Abre uma <strong>demanda urgente</strong> para o Time de Documentação (Revisão de
                documentos › Demandas urgentes).
              </span>
            </li>
            <li class="qualification-dialog__step">
              <span>O médico recebe push com o que falta e o prazo, com atalho para o Drive.</span>
            </li>
            <li class="qualification-dialog__step">
              <span>
                Documento aprovado ⇒ a alocação vira <strong>definitiva</strong> e o médico é
                avisado. Sem resolução até 24h antes do plantão, o médico é avisado; no início do
                plantão o <strong>check-in fica bloqueado</strong> e você decide manter ou remover.
                Nada é desalocado automaticamente.
              </span>
            </li>
          </ol>
        </div>
      </div>

      <footer class="qualification-dialog__footer">
        <OutlineButton
          height="44px"
          width="140px"
          color="grey"
          data-testid="pre-allocation-confirm-cancel"
          @click="cancel"
        >
          Cancelar
        </OutlineButton>
        <FilledButton
          height="44px"
          width="auto"
          color="primary"
          class="bg-primary30 px-4"
          data-testid="pre-allocation-confirm-submit"
          @click="confirm"
        >
          Pré-alocar e abrir demanda urgente
        </FilledButton>
      </footer>
    </div>
  </v-dialog>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { mdiAccountOutline, mdiClockOutline } from '@mdi/js'

import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import OutlineButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import type { EligibilityReason } from '@/types/qualification-eligibility.types'

import QualificationMissingList from './QualificationMissingList.vue'

const props = defineProps<{
  modelValue: boolean
  doctorName: string | null
  missing: EligibilityReason[]
  stationCount: number
}>()
const emit = defineEmits<{ 'update:modelValue': [value: boolean]; confirm: []; cancel: [] }>()

const isSingle = computed(() => props.stationCount === 1)

function confirm(): void {
  emit('confirm')
}

function cancel(): void {
  emit('update:modelValue', false)
  emit('cancel')
}

function onDialogUpdate(open: boolean): void {
  if (!open) cancel()
}
</script>

<style scoped src="./qualificationDialog.css"></style>
